<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { useQuepasaChannelApi } from '../../../../../composables/useQuepasaChannelApi';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const { endpoint, http } = useQuepasaChannelApi(() => props.inbox);

const formatDate = value => new Date(value).toLocaleString('pt-BR');

// ---------------------------------------------------------------------------
// Automatic synchronization (direct import — no n8n)
// ---------------------------------------------------------------------------
const autoSyncExpanded = ref(false);
const isSavingAutoSync = ref(false);
const autoSyncForm = reactive({
  enabled: true,
  interval: 5,
});
const lastAutoSyncAt = ref('');
const lastAutoSyncResult = ref(null);

const fetchConfig = async () => {
  try {
    const { data } = await http.get(endpoint('/config'));
    const attrs = data?.additional_attributes || {};
    autoSyncForm.enabled = ![false, 'false', 0, '0'].includes(
      attrs.auto_sync_enabled
    );
    autoSyncForm.interval = Number(attrs.auto_sync_interval_minutes) || 5;
    lastAutoSyncAt.value = attrs.last_auto_sync_at || '';
    lastAutoSyncResult.value = attrs.last_auto_sync_result || null;
  } catch (error) {
    // Non-blocking: panel simply renders defaults.
  }
};

const saveAutoSync = async () => {
  isSavingAutoSync.value = true;
  try {
    await http.post(endpoint('/config'), {
      config: {
        auto_sync_enabled: autoSyncForm.enabled,
        auto_sync_interval_minutes: Math.max(
          1,
          Number(autoSyncForm.interval) || 5
        ),
      },
    });
    useAlert(t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.SAVED'));
    await fetchConfig();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isSavingAutoSync.value = false;
  }
};

// ---------------------------------------------------------------------------
// Manual message synchronization
// ---------------------------------------------------------------------------
const messageSyncExpanded = ref(false);
const messageStats = ref(null);
const messagesCheckedAt = ref('');
const isCheckingMessages = ref(false);
const isSyncingMessages = ref(false);
const syncWindow = ref(1000);
const searchQuery = ref('');
const lastSyncResult = ref(null);

const checkMissingMessages = async () => {
  isCheckingMessages.value = true;
  try {
    const { data } = await http.get(endpoint('/sync/missing_messages'), {
      params: { limit: syncWindow.value },
    });
    messageStats.value = data;
    messagesCheckedAt.value = new Date().toISOString();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isCheckingMessages.value = false;
  }
};

const toggleMessagePanel = async () => {
  messageSyncExpanded.value = !messageSyncExpanded.value;
  if (messageSyncExpanded.value && !messageStats.value) {
    await checkMissingMessages();
  }
};

const syncAllMessages = async () => {
  isSyncingMessages.value = true;
  try {
    const { data } = await http.post(endpoint('/sync/sync_all'), {
      limit: syncWindow.value,
    });
    lastSyncResult.value = data;
    useAlert(
      t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.SYNC_RESULT', {
        redispatched: data?.redispatched || 0,
        failed: data?.failed || 0,
      })
    );
    await checkMissingMessages();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isSyncingMessages.value = false;
  }
};

// Client-side search over the missing-message list (id, contact, type, text).
const filteredMissing = computed(() => {
  const list = messageStats.value?.missing_messages || [];
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return list;
  return list.filter(message =>
    [message.id, message.from, message.type, message.text]
      .map(value => (value || '').toString().toLowerCase())
      .some(value => value.includes(query))
  );
});

// ---------------------------------------------------------------------------
// Contact synchronization + duplicates
// ---------------------------------------------------------------------------
const contactSyncExpanded = ref(false);
const contactStats = ref(null);
const contactsFetchedAt = ref('');
const isPreviewingContacts = ref(false);
const isSyncingContacts = ref(false);

const previewContactSync = async () => {
  isPreviewingContacts.value = true;
  try {
    const { data } = await http.get(endpoint('/contacts/sync/preview'));
    contactStats.value = data;
    contactsFetchedAt.value = new Date().toISOString();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isPreviewingContacts.value = false;
  }
};

const toggleContactPanel = async () => {
  contactSyncExpanded.value = !contactSyncExpanded.value;
  if (contactSyncExpanded.value && !contactStats.value) {
    await previewContactSync();
  }
};

// Tags applied to the imported contacts
const syncTags = ref(['quepasa', 'imported']);
const currentTag = ref('');

const addCurrentTag = () => {
  const value = currentTag.value.trim().replace(/[,\s]+$/, '');
  if (value && !syncTags.value.includes(value)) syncTags.value.push(value);
  currentTag.value = '';
};
const removeTag = index => syncTags.value.splice(index, 1);
const handleTagInput = event => {
  if (['Enter', ',', ' '].includes(event.key)) {
    event.preventDefault();
    addCurrentTag();
  } else if (event.key === 'Backspace' && !currentTag.value) {
    syncTags.value.pop();
  }
};
const handlePaste = event => {
  event.preventDefault();
  const pasted = (event.clipboardData || window.clipboardData).getData('text');
  pasted
    .split(/[,\s]+/)
    .map(tag => tag.trim())
    .filter(Boolean)
    .forEach(tag => {
      if (!syncTags.value.includes(tag)) syncTags.value.push(tag);
    });
};

const importableCount = () =>
  (contactStats.value?.new_contacts || 0) +
  (contactStats.value?.migratable_contacts || 0);

const syncContacts = async () => {
  isSyncingContacts.value = true;
  try {
    const { data } = await http.post(endpoint('/contacts/sync'), {
      tags: syncTags.value,
    });
    useAlert(
      t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.SYNC_RESULT', {
        created: data?.created || 0,
        migrated: data?.migrated || 0,
      })
    );
    await previewContactSync();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isSyncingContacts.value = false;
  }
};

// Duplicates
const mergeStats = ref(null);
const isPreviewingMerge = ref(false);
const isMerging = ref(false);

const previewMerge = async () => {
  isPreviewingMerge.value = true;
  try {
    const { data } = await http.get(endpoint('/contacts/merge/preview'));
    mergeStats.value = data;
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isPreviewingMerge.value = false;
  }
};

const mergeDuplicates = async () => {
  isMerging.value = true;
  try {
    const { data } = await http.post(endpoint('/contacts/merge/auto'));
    useAlert(
      t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.MERGE_RESULT', {
        merged: data?.merged || 0,
        errors: data?.error_count || 0,
      })
    );
    await previewMerge();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isMerging.value = false;
  }
};

onMounted(fetchConfig);
</script>

<template>
  <div class="flex flex-col gap-4 p-6">
    <!-- Automatic Sync Panel -->
    <div
      data-target="quepasa-channel-auto-sync-panel"
      class="order-3 border border-n-slate-6 rounded-lg overflow-hidden"
    >
      <button
        class="w-full flex items-center justify-between p-4 bg-n-slate-2 hover:bg-n-slate-3 transition-colors"
        @click="autoSyncExpanded = !autoSyncExpanded"
      >
        <div class="flex items-center gap-3">
          <fluent-icon
            icon="arrow-clockwise"
            size="20"
            class="text-n-slate-11"
          />
          <div class="text-left">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.TITLE') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.SUBTITLE') }}
            </p>
          </div>
        </div>
        <fluent-icon
          icon="chevron-down"
          size="20"
          class="text-n-slate-11 transition-transform"
          :class="{ 'rotate-180': autoSyncExpanded }"
        />
      </button>

      <div
        v-show="autoSyncExpanded"
        class="p-6 bg-n-slate-1 flex flex-col gap-4"
      >
        <label
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          <input
            v-model="autoSyncForm.enabled"
            type="checkbox"
            data-target="quepasa-channel-auto-sync-enabled"
          />
          {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.ENABLE') }}
        </label>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.METHOD') }}
          </label>
          <input
            type="text"
            disabled
            :value="
              t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.METHOD_VALUE')
            "
            class="px-3 py-2 text-sm border border-n-slate-6 rounded-md bg-n-slate-2 text-n-slate-11"
          />
          <p class="mb-0 text-xs text-n-slate-11">
            {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.METHOD_HELP') }}
          </p>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.INTERVAL') }}
          </label>
          <div class="grid grid-cols-2 gap-3">
            <input
              v-model.number="autoSyncForm.interval"
              type="number"
              min="1"
              data-target="quepasa-channel-auto-sync-interval"
              class="px-3 py-2 text-sm border border-n-slate-6 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
            />
            <input
              type="text"
              disabled
              :value="
                t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.INTERVAL_UNIT')
              "
              class="px-3 py-2 text-sm border border-n-slate-6 rounded-md bg-n-slate-2 text-n-slate-11"
            />
          </div>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.INTERVAL_HELP') }}
          </p>
        </div>

        <p
          v-if="lastAutoSyncAt"
          class="mb-0 text-xs text-n-slate-11"
          data-target="quepasa-channel-auto-sync-last"
        >
          {{
            t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.LAST_RUN', {
              at: formatDate(lastAutoSyncAt),
              redispatched: lastAutoSyncResult?.redispatched || 0,
              failed: lastAutoSyncResult?.failed || 0,
            })
          }}
        </p>

        <div class="flex justify-end">
          <NextButton
            data-target="quepasa-channel-auto-sync-save"
            :label="t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.AUTO.SAVE')"
            :is-loading="isSavingAutoSync"
            @click="saveAutoSync"
          />
        </div>
      </div>
    </div>

    <!-- Message Sync Expansion Panel -->
    <div
      data-target="quepasa-channel-message-sync-panel"
      class="order-1 border border-n-slate-6 rounded-lg overflow-hidden"
    >
      <button
        class="w-full flex items-center justify-between p-4 bg-n-slate-2 hover:bg-n-slate-3 transition-colors"
        @click="toggleMessagePanel"
      >
        <div class="flex items-center gap-3">
          <fluent-icon icon="mail" size="20" class="text-n-slate-11" />
          <div class="text-left">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.TITLE') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.SUBTITLE') }}
            </p>
          </div>
        </div>
        <fluent-icon
          icon="chevron-down"
          size="20"
          class="text-n-slate-11 transition-transform"
          :class="{ 'rotate-180': messageSyncExpanded }"
        />
      </button>

      <div v-show="messageSyncExpanded" class="p-6 bg-n-slate-1">
        <div class="flex flex-wrap items-end gap-3 mb-4">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.WINDOW') }}
            </label>
            <input
              v-model.number="syncWindow"
              type="number"
              min="1"
              class="px-3 py-2 text-sm border border-n-slate-6 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent w-40"
            />
          </div>
          <NextButton
            data-target="quepasa-channel-missing-messages"
            :label="t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.CHECK')"
            :is-loading="isCheckingMessages"
            @click="checkMissingMessages"
          />
        </div>
        <p class="mb-4 text-xs text-n-slate-11">
          {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.WINDOW_HELP') }}
        </p>

        <div v-if="messagesCheckedAt" class="flex items-center gap-2 mb-4">
          <div class="text-sm text-n-slate-11">
            {{
              t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.LAST_CHECKED', {
                at: formatDate(messagesCheckedAt),
              })
            }}
          </div>
        </div>

        <!-- Search filter over the missing list -->
        <input
          v-if="messageStats && messageStats.total > 0"
          v-model="searchQuery"
          type="text"
          data-target="quepasa-channel-missing-search"
          :placeholder="
            t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.SEARCH_PLACEHOLDER')
          "
          class="w-full mb-4 px-3 py-2 text-sm border border-n-slate-6 rounded-lg bg-n-slate-1 text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
        />

        <div
          v-if="messageStats"
          class="grid grid-cols-2 gap-4 mb-4 sm:grid-cols-2"
        >
          <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
            <div class="text-sm text-n-slate-11">
              {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.IN_CACHE') }}
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ messageStats.total_in_cache || 0 }}
            </div>
          </div>
          <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
            <div class="text-sm text-n-slate-11">
              {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.MISSING') }}
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ messageStats.total || 0 }}
            </div>
          </div>
          <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
            <div class="text-sm text-n-slate-11">
              {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.IGNORED') }}
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ messageStats.ignored || 0 }}
            </div>
          </div>
          <div
            class="p-4 rounded-lg bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700"
          >
            <div class="text-sm text-green-700 dark:text-green-300">
              {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.SYNCED') }}
            </div>
            <div
              class="text-2xl font-bold text-green-800 dark:text-green-100 mt-1"
            >
              {{ lastSyncResult?.redispatched || 0 }}
            </div>
          </div>
          <div
            class="p-4 rounded-lg bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700"
          >
            <div class="text-sm text-red-700 dark:text-red-300">
              {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.FAILED') }}
            </div>
            <div class="text-2xl font-bold text-red-800 dark:text-red-100 mt-1">
              {{ lastSyncResult?.failed || 0 }}
            </div>
          </div>
        </div>

        <!-- Missing messages list (searchable) -->
        <div
          v-if="messageStats && messageStats.total > 0"
          class="mb-4 max-h-80 overflow-y-auto border border-n-slate-6 rounded-lg divide-y divide-n-slate-6"
          data-target="quepasa-channel-missing-list"
        >
          <div
            v-for="message in filteredMissing"
            :key="message.id"
            class="p-3 text-sm flex flex-col gap-0.5"
          >
            <div class="flex items-center justify-between gap-2">
              <span class="font-mono text-xs text-n-slate-11 truncate">
                {{ message.id }}
              </span>
              <span class="text-xs text-n-slate-11 shrink-0">
                {{ message.timestamp ? formatDate(message.timestamp) : '' }}
              </span>
            </div>
            <div class="flex items-center gap-2 min-w-0">
              <span class="rounded bg-n-alpha-1 px-1.5 py-0.5 text-xs shrink-0">
                {{ message.type || 'text' }}
              </span>
              <span class="text-xs text-n-slate-11 truncate">
                {{ message.from }}
              </span>
            </div>
            <p
              v-if="message.text"
              class="mb-0 text-xs text-n-slate-12 truncate"
            >
              {{ message.text }}
            </p>
          </div>
          <p
            v-if="!filteredMissing.length"
            class="mb-0 p-4 text-center text-sm text-n-slate-11"
          >
            {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.SEARCH_EMPTY') }}
          </p>
        </div>

        <div
          v-if="messageStats && messageStats.total > 0"
          class="flex justify-center gap-4"
        >
          <NextButton
            data-target="quepasa-channel-sync-all"
            :label="
              t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.SYNC_ALL', {
                count: messageStats.total,
              })
            "
            :is-loading="isSyncingMessages"
            @click="syncAllMessages"
          />
        </div>

        <div
          v-else-if="messageStats && !isCheckingMessages"
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <fluent-icon
            icon="checkmark-circle"
            size="32"
            class="mb-4 text-emerald-600"
          />
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
            {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.NONE_MISSING_TITLE') }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('QUEPASA_CHANNEL.SETTINGS.MESSAGES_SYNC.NONE_MISSING') }}
          </p>
        </div>

        <div
          v-else-if="isCheckingMessages"
          class="flex items-center justify-center py-12"
        >
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-brand"
          />
        </div>
      </div>
    </div>

    <!-- Contact Sync Expansion Panel -->
    <div
      data-target="quepasa-channel-contact-sync-panel"
      class="order-2 border border-n-slate-6 rounded-lg overflow-hidden"
    >
      <button
        class="w-full flex items-center justify-between p-4 bg-n-slate-2 hover:bg-n-slate-3 transition-colors"
        @click="toggleContactPanel"
      >
        <div class="flex items-center gap-3">
          <fluent-icon icon="people" size="20" class="text-n-slate-11" />
          <div class="text-left">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.TITLE') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.SUBTITLE') }}
            </p>
          </div>
        </div>
        <fluent-icon
          icon="chevron-down"
          size="20"
          class="text-n-slate-11 transition-transform"
          :class="{ 'rotate-180': contactSyncExpanded }"
        />
      </button>

      <div v-show="contactSyncExpanded" class="p-6 bg-n-slate-1">
        <div class="flex items-center justify-between mb-6">
          <div v-if="contactsFetchedAt" class="text-sm text-n-slate-11">
            {{
              t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.LAST_FETCHED', {
                at: formatDate(contactsFetchedAt),
              })
            }}
          </div>
          <NextButton
            data-target="quepasa-channel-contacts-sync-preview"
            :label="t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.SYNC_PREVIEW')"
            :is-loading="isPreviewingContacts"
            @click="previewContactSync"
          />
        </div>

        <!-- Contact Stats -->
        <div v-if="contactStats" class="space-y-4 mb-6">
          <div class="grid grid-cols-2 gap-4">
            <div
              class="p-4 rounded-lg bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700"
            >
              <div class="text-sm text-blue-700 dark:text-blue-300">
                {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.NEW') }}
              </div>
              <div
                class="text-2xl font-bold text-blue-800 dark:text-blue-100 mt-1"
              >
                {{ contactStats.new_contacts || 0 }}
              </div>
            </div>
            <div
              class="p-4 rounded-lg bg-orange-50 dark:bg-orange-900 border border-orange-200 dark:border-orange-700"
            >
              <div class="text-sm text-orange-700 dark:text-orange-300">
                {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.MIGRATABLE') }}
              </div>
              <div
                class="text-2xl font-bold text-orange-800 dark:text-orange-100 mt-1"
              >
                {{ contactStats.migratable_contacts || 0 }}
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div
              class="p-4 rounded-lg bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700"
            >
              <div class="text-sm text-green-700 dark:text-green-300">
                {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.EXISTING') }}
              </div>
              <div
                class="text-2xl font-bold text-green-800 dark:text-green-100 mt-1"
              >
                {{ contactStats.existing_contacts || 0 }}
              </div>
            </div>
            <div
              class="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700"
            >
              <div class="text-sm text-yellow-700 dark:text-yellow-300">
                {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.IGNORED') }}
              </div>
              <div
                class="text-2xl font-bold text-yellow-800 dark:text-yellow-100 mt-1"
              >
                {{ contactStats.ignored_contacts || 0 }}
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div
          v-if="isPreviewingContacts && !contactStats"
          class="flex items-center justify-center py-12"
        >
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-brand"
          />
        </div>

        <!-- Tags Input -->
        <div v-if="contactStats && importableCount() > 0" class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.TAGS') }}
          </label>

          <div
            v-if="syncTags.length > 0"
            class="flex flex-wrap gap-2 mb-3 p-3 border border-n-slate-6 rounded-lg bg-n-slate-1"
          >
            <span
              v-for="(tag, index) in syncTags"
              :key="index"
              class="inline-flex items-center gap-1 px-2 py-1 bg-n-brand/10 text-n-brand rounded text-sm font-medium"
            >
              {{ tag }}
              <button
                type="button"
                class="ml-1 hover:text-red-600 focus:outline-none"
                @click="removeTag(index)"
              >
                <fluent-icon icon="dismiss" size="12" />
              </button>
            </span>
          </div>

          <input
            v-model="currentTag"
            data-target="quepasa-channel-sync-tags"
            type="text"
            :placeholder="
              t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.TAGS_PLACEHOLDER')
            "
            class="w-full px-3 py-2 border border-n-slate-6 rounded-lg bg-n-slate-1 text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent text-sm"
            @keydown="handleTagInput"
            @paste="handlePaste"
            @blur="addCurrentTag"
          />

          <p class="mb-0 text-xs text-n-slate-11 mt-1">
            {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.TAGS_HELP') }}
          </p>
        </div>

        <!-- Import Button -->
        <div
          v-if="contactStats && importableCount() > 0"
          class="flex justify-center mb-6"
        >
          <NextButton
            data-target="quepasa-channel-contacts-sync"
            :label="
              t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.SYNC_COUNT', {
                count: importableCount(),
              })
            "
            :is-loading="isSyncingContacts"
            @click="syncContacts"
          />
        </div>

        <!-- All synced -->
        <div
          v-else-if="contactStats && !isPreviewingContacts"
          class="flex flex-col items-center justify-center py-6 text-center"
        >
          <fluent-icon
            icon="checkmark-circle"
            size="32"
            class="mb-4 text-emerald-600"
          />
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.ALL_SYNCED') }}
          </p>
        </div>

        <!-- Duplicates -->
        <div class="pt-4 border-t border-n-slate-6">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-1">
            {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.DUPLICATES') }}
          </h4>
          <p class="mb-3 text-xs text-n-slate-11">
            {{ t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.DUPLICATES_HELP') }}
          </p>
          <div class="flex flex-wrap items-center gap-3">
            <NextButton
              size="sm"
              color-scheme="secondary"
              data-target="quepasa-channel-merge-preview"
              :label="t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.MERGE_PREVIEW')"
              :is-loading="isPreviewingMerge"
              @click="previewMerge"
            />
            <NextButton
              v-if="mergeStats && mergeStats.total > 0"
              size="sm"
              data-target="quepasa-channel-merge-duplicates"
              :label="
                t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.MERGE', {
                  count: mergeStats.total,
                })
              "
              :is-loading="isMerging"
              @click="mergeDuplicates"
            />
            <span v-if="mergeStats" class="text-sm text-n-slate-11">
              {{
                t('QUEPASA_CHANNEL.SETTINGS.CONTACTS.MERGE_PREVIEW_RESULT', {
                  count: mergeStats.total || 0,
                })
              }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
